<template>
  <v-container>
    <v-row>
      <v-col>
        <h1>Housekeeping</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <h2>Kollektenübertrag</h2>
        <p class="text--secondary">
          Organisationen mit KRO, deren virtuelles Bankkonto fehlt oder nicht mit der KRO übereinstimmt.
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table
          :headers="headers"
          :items="mismatchedOrganizations"
          :loading="loading"
          sort-by="name"
          no-data-text="Keine Abweichungen gefunden."
        >
          <template #item.name="{ item }">
            <router-link :to="{ name: 'management.org.details', params: { id: item.id } }">
              {{ item.name }}
            </router-link>
          </template>
          <template #item.realAccount="{ item }">
            <span v-if="item.realAccount">{{ item.realAccount }}</span>
            <span v-else class="text--disabled">fehlt</span>
          </template>
          <template #item.actual="{ item }">
            <span v-if="item.actual">{{ item.actual }}</span>
            <span v-else class="text--disabled">fehlt</span>
          </template>
          <template #item.reportingMode="{ item }">
            <v-chip small :color="item.reportingMode === 'pain_virtual_account' ? 'success' : undefined">
              {{ item.reportingMode === 'pain_virtual_account' ? 'aktiviert' : 'deaktiviert' }}
            </v-chip>
          </template>
          <template #item._actions="{ item }">
            <v-btn
              small
              text
              color="warning"
              :loading="!!fixing[item.id]"
              @click="fixVirtualAccount(item)"
            >
              <v-icon left small>{{ mdiAutorenew }}</v-icon>
              Korrigieren
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-divider class="mb-4"></v-divider>
        <h2>Kalender generieren</h2>
        <p class="text--secondary">
          Erzeugt die kirchlichen Feiertage/Termine (Kollektendaten) für ein Jahr. Ohne Auswahl
          einer Organisation wird der Kalender für alle Organisationen mit konfigurierten
          Kalendereinstellungen generiert. Bereits vorhandene Termine werden nicht verändert.
        </p>
        <v-row>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model.number="calendarYear"
              type="number"
              label="Jahr"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="calendarOrganizationIds"
              :items="organizationOptions"
              item-text="name"
              item-value="id"
              label="Organisationen (leer = alle konfigurierten)"
              multiple
              small-chips
              deletable-chips
              clearable
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="3" class="d-flex align-center">
            <v-btn
              color="primary"
              :loading="generatingCalendar"
              @click="generateCalendar"
            >
              <v-icon left>{{ mdiCalendarSync }}</v-icon>
              Generieren
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { mdiAutorenew, mdiCalendarSync } from '@mdi/js'
import { FETCH_ORGANIZATIONS } from '@/store/action-types'
import { SET_ORGANIZATION } from '@/store/mutation-types'
import { SET_SNACKBAR_MESSAGE } from '@/modules/common/store/mutation-types'
import { calculateVirtualAccount } from '@/lib/reconciliation-tools'
import { sortAlphaNumeric } from '@/lib/sort-utils'
import moment from 'moment'

export default {
  name: 'AdminReconciliation',
  data () {
    return {
      mdiAutorenew,
      mdiCalendarSync,
      fixing: {},
      calendarYear: moment().year() + 1,
      calendarOrganizationIds: [],
      generatingCalendar: false,
      headers: [
        { text: 'Organisation', value: 'name' },
        { text: 'KRO', value: 'kro' },
        { text: 'Echtes Bankkonto', value: 'realAccount' },
        { text: 'Erwartetes virtuelles Konto', value: 'expected' },
        { text: 'Aktuelles virtuelles Konto', value: 'actual' },
        { text: 'Kollektenübertrag', value: 'reportingMode' },
        { text: '', value: '_actions', sortable: false }
      ]
    }
  },
  computed: {
    ...mapState(['loading']),
    ...mapGetters('organization', ['allOrganizations']),
    mismatchedOrganizations () {
      return Object.values(this.allOrganizations)
        .map(org => {
          const kro = org.identifiers?.kro
          const expected = calculateVirtualAccount(kro)
          if (!expected) {
            return null
          }
          const actual = org.reconciliation_settings?.virtual_account || ''
          if (expected === actual) {
            return null
          }
          return {
            id: org.id,
            url: org.url,
            name: org.name,
            kro,
            realAccount: org.reconciliation_settings?.real_account || '',
            expected,
            actual,
            reportingMode: org.reconciliation_settings?.reporting_mode || 'none'
          }
        })
        .filter(Boolean)
    },
    organizationOptions () {
      return sortAlphaNumeric(
        Object.values(this.allOrganizations).filter(org => org.calendar_settings?.length),
        'name'
      )
    }
  },
  async created () {
    await this.$store.dispatch('organization/' + FETCH_ORGANIZATIONS)
  },
  methods: {
    async fixVirtualAccount (row) {
      this.$set(this.fixing, row.id, true)
      try {
        const org = this.allOrganizations[row.url]
        const settings = org.reconciliation_settings
        const payload = {
          reporting_mode: 'pain_virtual_account',
          real_account: settings?.real_account || null,
          virtual_account: row.expected
        }
        const method = settings?.id ? 'patch' : 'post'
        await this.$store.getters.restApi[method](`/organization/${row.id}/reconciliation_settings/`, payload)
        const response = await this.$store.getters.restApi.get(`/organization/${row.id}/`)
        this.$store.commit('organization/' + SET_ORGANIZATION, { data: response.data })
        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: 'Virtuelles Bankkonto korrigiert.',
          color: 'success'
        })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fix virtual account', e)
        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: 'Fehler beim Korrigieren des virtuellen Bankkontos.',
          color: 'error'
        })
      } finally {
        this.$set(this.fixing, row.id, false)
      }
    },
    async generateCalendar () {
      this.generatingCalendar = true
      try {
        await this.$store.getters.restApi.get('utils/make_calendar/', {
          params: {
            year: this.calendarYear,
            organizations: this.calendarOrganizationIds.length ? this.calendarOrganizationIds.join(',') : undefined
          }
        })
        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: `Kalender für ${this.calendarYear} wurde generiert.`,
          color: 'success'
        })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to generate calendar', e)
        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: 'Fehler beim Generieren des Kalenders.',
          color: 'error'
        })
      } finally {
        this.generatingCalendar = false
      }
    }
  }
}
</script>
